import React, { Dispatch, SetStateAction, PropsWithChildren } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Tab from "src/app/components/Tabs/Tab";
import Tabs from "src/app/components/Tabs/Tabs";
import { TaskType } from "src/types";
import { RootState } from "src/redux/reducers";
import {
  clearTaskList,
  setCreatorSearchLockedModalVisibility,
} from "src/redux";
import { useHasAccessAndIsEnabledCreatorSearch } from "../../hooks/useHasAccessAndIsEnabledCreatorSearch";
import ProjectBriefPreview from "./ProjectBriefPreview";
import ContentGuidelinesPreview from "./ContentGuidelinesPreview";
import useCheckWorkspacePermission from "src/hooks/useCheckWorkspacePermission";
import HorizontalScroller from "src/app/components/Tabs/components/HorizontalScroller";
import { getProject } from "src/redux/project/projectActions";

interface SingleProjectTopbarProps {
  setBoardHidden: Dispatch<SetStateAction<boolean>>;
  showCreatorSearchTab: boolean;
  handleCloseCreatorSearchTab: () => void;
  isActiveProject: boolean;
  projectId: string;
}

export default function SingleProjectTopbar({
  setBoardHidden,
  showCreatorSearchTab,
  handleCloseCreatorSearchTab,
  isActiveProject,
  children,
  projectId,
}: PropsWithChildren<SingleProjectTopbarProps>) {
  const tasksCounters = useSelector(
    (state: RootState) => state.projectReducer.tasksCounters,
  );

  const taskType = useSelector(
    (state: RootState) => state.projectReducer.taskType,
  );

  const isDemoWorkspace = useSelector(
    (state: RootState) => state.mainReducer.identity,
  )?.isDemoWorkspace;

  const isResearchCampaign =
    useSelector((state: RootState) => state.projectReducer.projectBasicData)
      ?.status === "research";

  const hasAccessAndIsEnabledCreatorSearch =
    useHasAccessAndIsEnabledCreatorSearch();
  const hasAccessToCreateCampaign =
    useCheckWorkspacePermission("CREATE_PROJECTS");
  const dispatch = useDispatch();
  const history = useHistory();

  const changeSearchParams = (search: string) => {
    history.push({
      search,
    });
  };

  const openCreatorSearchTab = () => {
    if (isDemoWorkspace) {
      dispatch(setCreatorSearchLockedModalVisibility(true));
      return;
    }

    dispatch(getProject(projectId));
    changeSearchParams("tab=creatorSearch");
  };

  const changeTab = (newTab: TaskType) => {
    if (newTab === taskType && !showCreatorSearchTab) {
      return;
    }

    handleCloseCreatorSearchTab();
    setBoardHidden(true);

    setTimeout(() => {
      dispatch(clearTaskList());
      changeSearchParams(`taskType=${newTab}`);
    }, 50);
  };

  const renderChildrenForCampaign = () => {
    if (showCreatorSearchTab) return null;

    return (
      <div className="projects__topbar-tasks-table-buttons">
        <HorizontalScroller>
          {taskType === TaskType.Content ||
          taskType === TaskType.Publication ? (
            <ContentGuidelinesPreview />
          ) : (
            <ProjectBriefPreview />
          )}
          {children}
        </HorizontalScroller>
      </div>
    );
  };

  const renderChildrenForCreatorSearch = () => {
    if (
      !showCreatorSearchTab ||
      !hasAccessToCreateCampaign ||
      !isResearchCampaign
    )
      return null;

    return children;
  };

  return (
    <div
      className={`projects__topbar projects__topbar--${showCreatorSearchTab ? "creator-search" : "tasks-table"}`}
    >
      <div className="projects__topbar projects__topbar--campaign">
        <Tabs useStylingForScroller>
          <HorizontalScroller>
            {hasAccessAndIsEnabledCreatorSearch && (
              <Tab
                tabText={
                  <IDHFormattedMessage
                    id="ws_creator_search"
                    defaultMessage="Creator Search"
                  />
                }
                active={showCreatorSearchTab}
                onClick={openCreatorSearchTab}
              />
            )}
            {isActiveProject && (
              <>
                <Tab
                  tabText={
                    <IDHFormattedMessage
                      id="ws_creators"
                      defaultMessage="Creators"
                    />
                  }
                  active={
                    !showCreatorSearchTab && taskType === TaskType.Creator
                  }
                  onClick={() => changeTab(TaskType.Creator)}
                  count={tasksCounters?.creators}
                />
                <Tab
                  tabText={
                    <IDHFormattedMessage
                      id="ws_content"
                      defaultMessage="Content"
                    />
                  }
                  active={
                    !showCreatorSearchTab && taskType === TaskType.Content
                  }
                  onClick={() => changeTab(TaskType.Content)}
                  count={tasksCounters?.content}
                />
                <Tab
                  tabText={
                    <IDHFormattedMessage
                      id="ws_publications"
                      defaultMessage="Publications"
                    />
                  }
                  active={
                    !showCreatorSearchTab && taskType === TaskType.Publication
                  }
                  onClick={() => changeTab(TaskType.Publication)}
                  count={tasksCounters?.publications}
                />
                <Tab
                  tabText={
                    <IDHFormattedMessage
                      id="ws_payments"
                      defaultMessage="Payments"
                    />
                  }
                  active={
                    !showCreatorSearchTab && taskType === TaskType.Payment
                  }
                  onClick={() => changeTab(TaskType.Payment)}
                  count={tasksCounters?.payments}
                />
                <Tab
                  tabText={
                    <IDHFormattedMessage
                      id="ws_actions"
                      defaultMessage="Actions"
                    />
                  }
                  active={!showCreatorSearchTab && taskType === TaskType.Action}
                  onClick={() => changeTab(TaskType.Action)}
                  count={tasksCounters?.actions}
                />
              </>
            )}
          </HorizontalScroller>

          {renderChildrenForCreatorSearch()}
        </Tabs>

        {renderChildrenForCampaign()}
      </div>
    </div>
  );
}
